// UI / theming preferences. Persisted to localStorage so the canvas looks the
// same across reloads. Read by index.css var setters and TweaksPanel.

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type BgPattern = "dots" | "grid" | "blueprint" | "plain";

// Side-panel sizing constraints. The studio canvas needs a minimum width to
// stay usable, so we clamp panels to a sensible range.
export const PANEL_MIN_WIDTH = 200;
export const PANEL_MAX_WIDTH = 480;
export const PALETTE_DEFAULT_WIDTH = 248;
export const INSPECTOR_DEFAULT_WIDTH = 280;

const clampPanel = (n: number) =>
  Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, Math.round(n)));

interface UiState {
  dark: boolean;
  hueShift: number; // 0..360, applied on top of base hues
  bg: BgPattern;
  personaSlug: string; // active persona for the palette (M2)
  paletteWidth: number;
  inspectorWidth: number;
  paletteCollapsed: boolean;
  inspectorCollapsed: boolean;
  setDark: (dark: boolean) => void;
  setHueShift: (h: number) => void;
  setBg: (bg: BgPattern) => void;
  setPersonaSlug: (slug: string) => void;
  setPaletteWidth: (w: number) => void;
  setInspectorWidth: (w: number) => void;
  setPaletteCollapsed: (c: boolean) => void;
  setInspectorCollapsed: (c: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      dark: false,
      hueShift: 0,
      bg: "dots",
      personaSlug: "swe",
      paletteWidth: PALETTE_DEFAULT_WIDTH,
      inspectorWidth: INSPECTOR_DEFAULT_WIDTH,
      paletteCollapsed: false,
      inspectorCollapsed: false,
      setDark: (dark) => set({ dark }),
      setHueShift: (hueShift) => set({ hueShift }),
      setBg: (bg) => set({ bg }),
      setPersonaSlug: (personaSlug) => set({ personaSlug }),
      setPaletteWidth: (w) => set({ paletteWidth: clampPanel(w) }),
      setInspectorWidth: (w) => set({ inspectorWidth: clampPanel(w) }),
      setPaletteCollapsed: (paletteCollapsed) => set({ paletteCollapsed }),
      setInspectorCollapsed: (inspectorCollapsed) =>
        set({ inspectorCollapsed }),
    }),
    { name: "harness-ui" },
  ),
);

/**
 * Sync the dark/hue values into CSS custom properties so plain-CSS bits
 * (index.css, OKLCH chips) pick them up. Called once on App mount.
 */
export function applyTheme(dark: boolean, hueShift: number) {
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  // Accent hue shifts the base 268 used in index.css.
  const accentH = (268 + hueShift) % 360;
  root.style.setProperty("--accent-h", String(accentH));
}
